import {
  Box,
  Flex,
  HStack,
  Image,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { NodeOperator } from "../../rest/kairos/types";
import { ThemeColor } from "../../theme/palette";
import { formatAddress } from "../../utils/format";
import {
  DRIP_BOX_BG,
  DRIP_BORDER,
  DRIP_TEXT_LABEL,
  DRIP_TEXT_MUTED,
} from "../../features/drip/dripStyles";

interface DashboardTableProps {
  nodeOperators: NodeOperator[];
  isLoading: boolean;
}

interface VaultConfig {
  symbol: string;
  image: string;
  staked: (no: NodeOperator) => string | null;
  cooling: (no: NodeOperator) => string | null;
  queued: (no: NodeOperator) => string | null;
}

const VAULTS: VaultConfig[] = [
  {
    symbol: "kySOL",
    image: "/kysol.svg",
    staked: (no) => no.kySOLStaked,
    cooling: (no) => no.kySOLCooling,
    queued: (no) => no.kySOLQueued,
  },
  {
    symbol: "kyJTO",
    image: "/kyjto.svg",
    staked: (no) => no.kyJTOStaked,
    cooling: (no) => no.kyJTOCooling,
    queued: (no) => no.kyJTOQueued,
  },
];

const toNumber = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const fmtAmount = (value: number): string =>
  value.toLocaleString("en-US", { maximumFractionDigits: 2 });

// Zero cooling/queued is the common case — render it as a quiet dash so real
// activity stands out.
const SecondaryAmount = ({ value }: { value: number }) => (
  <Text color={value > 0 ? "whiteAlpha.800" : "whiteAlpha.400"}>
    {value > 0 ? fmtAmount(value) : "—"}
  </Text>
);

const RestakingVaultCard = ({
  vault,
  nodeOperators,
}: {
  vault: VaultConfig;
  nodeOperators: NodeOperator[];
}) => {
  // Only operators that actually hold TVL in this vault, largest first.
  const operators = nodeOperators
    .filter((no) => toNumber(vault.staked(no)) > 0)
    .sort((a, b) => toNumber(vault.staked(b)) - toNumber(vault.staked(a)));

  if (operators.length === 0) return null;

  const totalStaked = operators.reduce((sum, no) => sum + toNumber(vault.staked(no)), 0);
  const topStaked = toNumber(vault.staked(operators[0]));

  return (
    <Box
      bg={DRIP_BOX_BG}
      borderRadius="xl"
      border="1px solid"
      borderColor={DRIP_BORDER}
      overflow="hidden"
      w="100%"
    >
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 5, md: 6 }}
        py={5}
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <HStack spacing={3} align="center">
          <Image src={vault.image} alt={vault.symbol} boxSize="32px" borderRadius="full" />
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="semibold" color="white">
              {vault.symbol}
            </Text>
            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
              {operators.length} active operator{operators.length > 1 ? "s" : ""}
            </Text>
          </VStack>
        </HStack>
        <VStack align="end" spacing={0}>
          <Text fontSize="xs" color={DRIP_TEXT_LABEL} letterSpacing="0.08em">
            STAKED
          </Text>
          <HStack spacing={1.5} align="baseline">
            <Text fontSize="xl" fontWeight="semibold" color={ThemeColor.KAIROS}>
              {fmtAmount(totalStaked)}
            </Text>
            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
              {vault.symbol}
            </Text>
          </HStack>
        </VStack>
      </Flex>

      <TableContainer px={{ base: 5, md: 6 }} pb={2}>
        <Table variant="info" sx={{ "tbody td": { py: 5 } }}>
          <Thead>
            <Tr>
              <Th>Operator</Th>
              <Th>ID</Th>
              <Th isNumeric>Staked</Th>
              <Th isNumeric>Cooling</Th>
              <Th isNumeric>Queued</Th>
            </Tr>
          </Thead>
          <Tbody>
            {operators.map((no, index) => {
              const staked = toNumber(vault.staked(no));
              const share = totalStaked > 0 ? (staked / totalStaked) * 100 : 0;
              // Bar is scaled against the leader so dominance is easy to read.
              const barWidth = topStaked > 0 ? (staked / topStaked) * 100 : 0;
              return (
                <Tr
                  key={`${no.id}-${index}`}
                  sx={{
                    _hover: { backgroundColor: "whiteAlpha.100" },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <Td verticalAlign="middle">
                    <HStack spacing={2.5} align="center">
                      <Text
                        fontSize="xs"
                        color={DRIP_TEXT_MUTED}
                        w="16px"
                        textAlign="right"
                        flexShrink={0}
                      >
                        {index + 1}
                      </Text>
                      <Image
                        src={no.image}
                        alt={no.name}
                        boxSize="24px"
                        borderRadius="full"
                        flexShrink={0}
                      />
                      <Text color="white" noOfLines={1}>
                        {no.name}
                      </Text>
                    </HStack>
                  </Td>
                  <Td verticalAlign="middle">
                    <Text color={DRIP_TEXT_MUTED} fontFamily="mono" fontSize="sm">
                      {no.id ? formatAddress(no.id, 4, 4) : "—"}
                    </Text>
                  </Td>
                  <Td isNumeric verticalAlign="middle">
                    <VStack align="end" spacing={1.5}>
                      <HStack spacing={2} align="baseline">
                        <Text color="white" fontWeight="medium">
                          {fmtAmount(staked)}
                        </Text>
                        <Text fontSize="xs" color={DRIP_TEXT_MUTED} w="42px">
                          {share.toFixed(1)}%
                        </Text>
                      </HStack>
                      <Box
                        w="100%"
                        maxW="120px"
                        h="3px"
                        bg="whiteAlpha.100"
                        borderRadius="full"
                        overflow="hidden"
                      >
                        <Box h="100%" w={`${barWidth}%`} bg={ThemeColor.KAIROS} opacity={0.7} />
                      </Box>
                    </VStack>
                  </Td>
                  <Td isNumeric verticalAlign="middle">
                    <SecondaryAmount value={toNumber(vault.cooling(no))} />
                  </Td>
                  <Td isNumeric verticalAlign="middle">
                    <SecondaryAmount value={toNumber(vault.queued(no))} />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const DashboardTable = ({ nodeOperators, isLoading }: DashboardTableProps) => {
  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch" w="100%">
        <Skeleton height="220px" borderRadius="xl" />
        <Skeleton height="220px" borderRadius="xl" />
      </VStack>
    );
  }

  const cards = VAULTS.map((vault) => (
    <RestakingVaultCard key={vault.symbol} vault={vault} nodeOperators={nodeOperators} />
  )).filter(Boolean);

  const hasContent = cards.some((card) => card !== null);

  if (!hasContent) {
    return (
      <Box
        bg={DRIP_BOX_BG}
        borderRadius="xl"
        border="1px solid"
        borderColor={DRIP_BORDER}
        px={6}
        py={8}
      >
        <Text fontSize="sm" color={DRIP_TEXT_MUTED}>
          No restaking vaults currently hold TVL.
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {cards}
    </VStack>
  );
};
